import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import AvantageGroupeRabais from '../components/AvantageGroupeRabais';
import {
  Typography,
  Box,
  Grid,
  Button,
  Stack,
  Card,
  CardContent,
  CardActions,
  Fab,
  Fade,
  Collapse,
  useMediaQuery,
} from '@mui/material';
import { styled, keyframes, useTheme } from '@mui/material/styles';
import { Link, useNavigate } from 'react-router-dom';
import CheckCircleIcon     from '@mui/icons-material/CheckCircle';
import AccessTimeIcon      from '@mui/icons-material/AccessTime';
import AttachMoneyIcon     from '@mui/icons-material/AttachMoney';
import GroupsIcon          from '@mui/icons-material/Groups';
import ArrowForwardIcon    from '@mui/icons-material/ArrowForward';
import StarIcon            from '@mui/icons-material/Star';
import SchoolIcon          from '@mui/icons-material/School';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InfoIcon            from '@mui/icons-material/Info';
import ExpandLessIcon      from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon      from '@mui/icons-material/ExpandMore';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import VerifiedIcon         from '@mui/icons-material/Verified';

import heroFormations from '../assets/image1.png';
import TemplateImg    from '../assets/Template.png';

// ─── Animations ────────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const bounceY = keyframes`
  0%,100% { transform: translateY(0); }
  50%      { transform: translateY(8px); }
`;

// ─── Helpers ───────────────────────────────────────────────────────────────────
const getImageSrc = (img) => {
  if (!img) return '';
  if (img.startsWith('http') || img.startsWith('//')) return img;
  const base = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/api$/, '');
  return img.startsWith('/') ? `${base}${img}` : `${base}/${img}`;
};

const parseArray = (val) => {
  if (Array.isArray(val)) return val;
  if (val == null) return [];
  if (typeof val === 'string' && val.trim() === '') return [];
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === 'string') {
        try {
          const dp = JSON.parse(parsed);
          return Array.isArray(dp) ? dp : dp ? [dp] : [];
        } catch { return parsed ? [parsed] : []; }
      }
      return parsed ? [parsed] : [];
    } catch {
      if (val.includes(',')) return val.split(',').map(s => s.trim()).filter(Boolean);
      return [val];
    }
  }
  return val ? [val] : [];
};

const MODE_MAP = {
  presentiel:    'Présentiel',
  en_ligne:      'Visioconférence',
  auto_formation:'En ligne (SANS CERTIFICATION)',
  mixte:         'Mixte',
};
const getMode = (m) => MODE_MAP[m] || m;

const MODE_COLOR = {
  presentiel:    '#2e7d32',
  en_ligne:      '#1565c0',
  mixte:         '#e65100',
  auto_formation:'#6a1b9a',
};
const getModeColor = (m) => MODE_COLOR[m] || '#555';

// ─── Styled ────────────────────────────────────────────────────────────────────
const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  background: `linear-gradient(150deg, rgba(183,28,28,0.97) 0%, rgba(90,0,0,0.93) 100%), url(${heroFormations})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  color: '#fff',
  padding: theme.spacing(16, 0, 13),
  textAlign: 'center',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -1, left: 0, right: 0,
    height: '70px',
    background: '#f7f8fc',
    clipPath: 'ellipse(60% 100% at 50% 100%)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-80px', right: '-80px',
    width: 360, height: 360, borderRadius: '50%',
    background: 'rgba(255,255,255,0.05)',
    pointerEvents: 'none',
  },
}));

const PageWrap = styled(Box)(({ theme }) => ({
  backgroundColor: '#f7f8fc',
  padding: theme.spacing(7, 0, 10),
}));

const Wrap = styled(Box)(({ theme }) => ({
  maxWidth: '1200px',
  margin: '0 auto',
  padding: theme.spacing(0, 3),
}));

const SectionLabel = styled(Typography)(() => ({
  fontSize: '0.78rem', fontWeight: 700,
  letterSpacing: '0.2em', textTransform: 'uppercase',
  color: '#c62828', marginBottom: '10px', display: 'block', textAlign: 'center',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: 'relative',
  paddingBottom: theme.spacing(2.5),
  marginBottom: theme.spacing(4),
  textAlign: 'center',
  fontWeight: 800,
  color: '#1a1a1a',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0, left: '50%',
    transform: 'translateX(-50%)',
    width: '44px', height: '4px',
    background: 'linear-gradient(90deg, #c62828, #ff6b6b)',
    borderRadius: '2px',
  },
}));

const CategoryBox = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '20px',
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3.5),
  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
  border: '1px solid rgba(0,0,0,0.06)',
}));

const FormationCardStyled = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '16px',
  overflow: 'hidden',
  border: '1px solid rgba(0,0,0,0.06)',
  boxShadow: '0 3px 16px rgba(0,0,0,0.06)',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 14px 36px rgba(0,0,0,0.12)',
  },
}));

const ScrollBtn = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  zIndex: 1001,
  backgroundColor: '#c62828',
  color: 'white',
  boxShadow: '0 6px 20px rgba(198,40,40,0.35)',
  '&:hover': { backgroundColor: '#8e0000' },
}));

// ─── Composant Carte Catégorie (logique inchangée) ────────────────────────────
const CategoryCard = ({ category, count, onClick, isSelected }) => {
  const imageUrl = category.image ? getImageSrc(category.image) : TemplateImg;
  return (
    <Box
      onClick={() => onClick(category.id)}
      sx={{
        position: 'relative',
        width: '100%',
        paddingTop: '100%',
        borderRadius: '16px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        boxShadow: isSelected ? '0 8px 28px rgba(198,40,40,0.45)' : '0 4px 16px rgba(0,0,0,0.12)',
        border: isSelected ? '3px solid #c62828' : '2px solid transparent',
        '&:hover': {
          transform: 'translateY(-6px)',
          boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
          '& .cat-overlay': { backgroundColor: 'rgba(0,0,0,0.6)' },
        },
      }}
    >
      <img src={imageUrl} alt={category.nom} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      <Box
        className="cat-overlay"
        sx={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: isSelected ? 'rgba(198,40,40,0.65)' : 'rgba(0,0,0,0.48)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'background-color 0.3s', p: 2,
          flexDirection: 'column', gap: 1,
        }}
      >
        <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 800, textAlign: 'center', textShadow: '0 2px 6px rgba(0,0,0,0.6)', lineHeight: 1.3, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
          {category.nom}
        </Typography>
        {count > 0 && (
          <Box sx={{ backgroundColor: 'rgba(255,255,255,0.92)', color: isSelected ? '#c62828' : '#333', borderRadius: '20px', px: 1.25, py: 0.3, fontSize: '0.72rem', fontWeight: 700, boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}>
            {count} formation{count > 1 ? 's' : ''}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─── Composant Carte Formation (logique inchangée, visuel amélioré) ───────────
const FormationCard = ({ formation, onFilterByCategory }) => {
  const [showDetails, setShowDetails] = useState(false);
  const navigate = useNavigate();
  const lienDetail = `/formations/${formation.id}`;

  // Toute la carte mène à la page de la formation, comme « En savoir plus » :
  // beaucoup de visiteurs cliquaient sur la carte sans rien obtenir. Seuls
  // « Voir les détails » (qui déplie la carte) et la pastille de catégorie
  // (qui filtre) gardent leur propre action.
  const ouvrir = () => {
    // Pas de navigation si la personne sélectionnait du texte.
    if (window.getSelection?.().toString()) return;
    navigate(lienDetail);
  };
  const actionPropre = (action) => (e) => {
    e.stopPropagation();
    action();
  };
  const competences = Array.isArray(formation.competences_enseignees) ? formation.competences_enseignees : [];
  const publicCible = Array.isArray(formation.public_cible) ? formation.public_cible : [];
  const avantages   = Array.isArray(formation.avantages) ? formation.avantages : [];
  const hasDetails  = competences.length > 0 || publicCible.length > 0 || avantages.length > 0;
  const modeColor   = getModeColor(formation.mode);

  const truncate = (text, max = 110) => !text ? '' : text.length <= max ? text : text.slice(0, max) + '…';

  return (
    <FormationCardStyled
      role="link"
      tabIndex={0}
      aria-label={`${formation.titre} — voir la formation`}
      onClick={ouvrir}
      onKeyDown={(e) => { if (e.key === 'Enter') navigate(lienDetail); }}
    >
      {/* Image */}
      {formation.image ? (
        <Box sx={{ position: 'relative', overflow: 'hidden', height: 170 }}>
          <Box component="img" src={getImageSrc(formation.image)} alt={formation.titre}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s', '&:hover': { transform: 'scale(1.05)' } }}
          />
          {/* Badge mode */}
          <Box sx={{
            position: 'absolute', top: 10, right: 10,
            backgroundColor: modeColor, color: '#fff',
            borderRadius: '20px', px: 1.25, py: 0.4,
            fontSize: '0.68rem', fontWeight: 700, boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          }}>
            {getMode(formation.mode)}
          </Box>
        </Box>
      ) : (
        <Box sx={{ height: 170, bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
          <SchoolIcon sx={{ fontSize: 52, color: 'rgba(0,0,0,0.1)' }} />
          <Box sx={{ position: 'absolute', top: 10, right: 10, backgroundColor: modeColor, color: '#fff', borderRadius: '20px', px: 1.25, py: 0.4, fontSize: '0.68rem', fontWeight: 700 }}>
            {getMode(formation.mode)}
          </Box>
        </Box>
      )}

      <CardContent sx={{ p: 2.5, flexGrow: 1 }}>
        {/* Catégorie */}
        {formation.categorie && (
          <Box
            onClick={actionPropre(() => onFilterByCategory(formation.categorie.id))}
            sx={{
              display: 'inline-block',
              background: `${formation.categorie.couleur || '#c62828'}18`,
              color: formation.categorie.couleur || '#c62828',
              px: 1.25, py: 0.3,
              borderRadius: '20px', fontSize: '0.7rem', fontWeight: 700, mb: 1.25,
              cursor: 'pointer', border: `1px solid ${formation.categorie.couleur || '#c62828'}33`,
            }}
          >
            {formation.categorie.nom}
          </Box>
        )}

        {/* Titre */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a', fontSize: '0.97rem', lineHeight: 1.35, mb: 0.75, minHeight: '40px' }}>
          {formation.titre}
        </Typography>
        {formation.sous_titre && (
          <Typography variant="body2" sx={{ color: '#888', fontSize: '0.78rem', mb: 1 }}>
            {formation.sous_titre}
          </Typography>
        )}

        {/* Description */}
        <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.65, mb: 2, fontSize: '0.84rem', minHeight: '55px' }}>
          {truncate(formation.description)}
        </Typography>

        {/* Badges durée + tarif */}
        <Stack direction="row" spacing={1} sx={{ mb: hasDetails ? 1.5 : 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, background: 'rgba(198,40,40,0.07)', px: 1.25, py: 0.5, borderRadius: '8px' }}>
            <AccessTimeIcon sx={{ fontSize: '0.85rem', color: '#c62828' }} />
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#c62828' }}>{formation.heure}h</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, background: 'rgba(198,40,40,0.07)', px: 1.25, py: 0.5, borderRadius: '8px' }}>
            <AttachMoneyIcon sx={{ fontSize: '0.85rem', color: '#c62828' }} />
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#c62828' }}>{formation.tarif} $</Typography>
          </Box>
        </Stack>

        {/* Détails dépliables */}
        {hasDetails && (
          <>
            <Button size="small" onClick={actionPropre(() => setShowDetails(!showDetails))}
              startIcon={<InfoIcon sx={{ fontSize: '0.85rem' }} />}
              endIcon={showDetails ? <ExpandLessIcon sx={{ fontSize: '0.85rem' }} /> : <ExpandMoreIcon sx={{ fontSize: '0.85rem' }} />}
              sx={{ color: '#555', fontWeight: 600, fontSize: '0.78rem', textTransform: 'none', p: 0.5, mt: 0.5, '&:hover': { background: 'rgba(198,40,40,0.05)' } }}
            >
              {showDetails ? 'Masquer' : 'Voir les détails'}
            </Button>

            <Collapse in={showDetails}>
              <Box
                onClick={(e) => e.stopPropagation()}
                sx={{ mt: 1.5, p: 2, borderRadius: '10px', background: '#fafafa', border: '1px solid rgba(0,0,0,0.05)', cursor: 'default' }}
              >
                {competences.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#1a1a1a', display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                      Compétences enseignées
                    </Typography>
                    <Stack spacing={0.5}>
                      {competences.slice(0, 3).map((s, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                          <CheckCircleIcon sx={{ fontSize: '0.8rem', color: '#c62828', mt: 0.2, flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ fontSize: '0.78rem', color: '#555', lineHeight: 1.5 }}>{s}</Typography>
                        </Box>
                      ))}
                      {competences.length > 3 && <Typography variant="caption" sx={{ color: '#999', ml: 1.75 }}>+ {competences.length - 3} autres</Typography>}
                    </Stack>
                  </Box>
                )}
                {publicCible.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#1a1a1a', display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                      Public cible
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {publicCible.slice(0, 4).map((p, i) => (
                        <Box key={i} sx={{ background: 'rgba(25,118,210,0.08)', color: '#1565c0', border: '1px solid rgba(25,118,210,0.2)', borderRadius: '20px', px: 1, py: 0.2, fontSize: '0.7rem', fontWeight: 600 }}>
                          {p}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
                {avantages.length > 0 && (
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#1a1a1a', display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                      Avantages
                    </Typography>
                    <Stack spacing={0.5}>
                      {avantages.slice(0, 2).map((av, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                          <StarIcon sx={{ fontSize: '0.8rem', color: '#f59e0b', mt: 0.2, flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ fontSize: '0.78rem', color: '#555' }}>{av}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            </Collapse>
          </>
        )}
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0 }}>
        <Button
          component={Link}
          to={lienDetail}
          onClick={(e) => e.stopPropagation()}
          fullWidth variant="contained"
          endIcon={<ArrowForwardIcon />}
          sx={{
            background: 'linear-gradient(135deg, #c62828, #8e0000)',
            color: '#fff', fontWeight: 700, py: 1, borderRadius: '10px',
            textTransform: 'none', fontSize: '0.875rem',
            '&:hover': { background: 'linear-gradient(135deg, #b71c1c, #7f0000)' },
          }}
        >
          En savoir plus
        </Button>
      </CardActions>
    </FormationCardStyled>
  );
};

// ─── Composant Principal ──────────────────────────────────────────────────────
const Formations = () => {
  const [formations, setFormations]         = useState([]);
  const [categories, setCategories]         = useState([]);
  const [loading, setLoading]               = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  // Début du catalogue : on y fait défiler l'écran quand une catégorie est choisie.
  const catalogueRef = useRef(null);

  // Nombre maximal de catégories par rangée selon la largeur d'écran.
  const theme = useTheme();
  const ecranLarge = useMediaQuery(theme.breakpoints.up('lg'));
  const ecranMoyen = useMediaQuery(theme.breakpoints.up('md'));
  const ecranPetit = useMediaQuery(theme.breakpoints.up('sm'));
  const maxParRangee = ecranLarge ? 6 : ecranMoyen ? 4 : ecranPetit ? 3 : 2;

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [fRes, cRes] = await Promise.all([
          axios.get(`${API_URL}/formations`),
          axios.get(`${API_URL}/categories/active`),
        ]);
        const fetchedFormations = (fRes.data.success ? fRes.data.data : []).map(f => ({
          ...f,
          competences_enseignees: parseArray(f.competences_enseignees),
          public_cible: parseArray(f.public_cible),
          avantages: parseArray(f.avantages),
        }));
        setFormations(fetchedFormations);
        setCategories(cRes.data.success ? cRes.data.data : []);
      } catch (err) {
        console.error('Erreur chargement', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const h = () => setShowScrollButton(window.scrollY > 400);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  const filteredFormations = selectedCategory
    ? formations.filter(f => f.categorie?.id === selectedCategory)
    : formations;

  const groupedByCategoryId = {};
  categories.forEach(cat => {
    const catF = filteredFormations.filter(f => f.categorie?.id === cat.id);
    if (catF.length > 0) groupedByCategoryId[cat.id] = catF;
  });
  const autres = filteredFormations.filter(f => !f.categorie?.id || !groupedByCategoryId[f.categorie.id]);

  const onFilterByCategory = (catId) => {
    setSelectedCategory(prev => (prev === catId ? null : catId));
    // Après le nouvel affichage, on descend jusqu'aux formations filtrées :
    // sans ça, le filtre s'applique plus bas, hors de l'écran, et le visiteur
    // croit que rien ne s'est passé. (Remplace un défilement fixe à 680 px,
    // faux dès que la hauteur de la page changeait.)
    setTimeout(() => catalogueRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };

  // Catégories affichées (celles qui ont au moins une formation), réparties en
  // rangées égales : 6 catégories donnent 3 + 3 plutôt que 5 + 1. Une rangée
  // incomplète est centrée.
  const categoriesVisibles = categories
    .map(cat => ({ cat, count: formations.filter(f => f.categorie?.id === cat.id).length }))
    .filter(({ count }) => count > 0);
  const nbRangees = Math.max(1, Math.ceil(categoriesVisibles.length / maxParRangee));
  const parRangee = Math.max(1, Math.ceil(categoriesVisibles.length / nbRangees));

  const selectedCatName = selectedCategory
    ? categories.find(c => c.id === selectedCategory)?.nom || null
    : null;

  return (
    <>
      {/* ══════════════════ HERO ══════════════════ */}
      <HeroSection>
        <Box sx={{ position: 'absolute', bottom: '80px', left: '-50px', width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
        <Wrap sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ animation: `${fadeUp} 0.8s ease both` }}>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2.5, py: 0.8, borderRadius: '50px', border: '1px solid rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(4px)', mb: 3, color: '#fff' }}>
              <VerifiedIcon sx={{ fontSize: '1rem' }} />
              <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: '0.08em' }}>Certifiées · Croix-Rouge canadienne</Typography>
            </Box>
            <Typography variant="h1" sx={{ fontSize: { xs: '2.4rem', md: '3.6rem' }, fontWeight: 900, mb: 2.5, lineHeight: 1.1 }}>
              Nos formations de secourisme
            </Typography>
            <Typography sx={{ maxWidth: '600px', mx: 'auto', fontSize: '1.1rem', lineHeight: 1.85, opacity: 0.88, mb: 5 }}>
              Formations reconnues par la Croix-Rouge canadienne. Pratiques, accessibles et adaptées à tous les profils.
            </Typography>
            <Button component={Link} to="/contact" variant="contained" size="large" endIcon={<ArrowForwardIcon />}
              sx={{ background: '#fff', color: '#c62828', fontWeight: 800, px: 4.5, py: 1.5, borderRadius: '50px', boxShadow: '0 8px 24px rgba(0,0,0,0.2)', '&:hover': { background: 'rgba(255,255,255,0.92)', transform: 'translateY(-2px)' } }}>
              Demander un devis groupe
            </Button>
          </Box>
          <Box sx={{ mt: 5, animation: `${bounceY} 2.2s ease infinite` }}>
            <KeyboardArrowDownIcon sx={{ fontSize: 28, opacity: 0.5 }} />
          </Box>
        </Wrap>
      </HeroSection>

      {/* ══════════════════ CORPS ══════════════════ */}
      <PageWrap>
        <Wrap>

          {/* ── Bannière inscription de groupe ── */}
          <Box sx={{
            mb: 5,
            p: { xs: 2.5, md: 3 },
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #8e0000 0%, #c62828 60%, #e53935 100%)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2.5,
            boxShadow: '0 6px 24px rgba(198,40,40,0.3)',
          }}>
            <Box sx={{
              width: 48, height: 48, flexShrink: 0,
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.15)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <GroupsIcon sx={{ fontSize: 26, color: '#fff' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '0.95rem', md: '1rem' }, mb: 0.3 }}>
                Vous représentez une entreprise ou un organisme ?
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.82)', fontSize: '0.84rem', lineHeight: 1.5, mb: 0.75 }}>
                Inscrivez votre groupe — notre équipe vous organisera une session adaptée à vos besoins.
              </Typography>
              <AvantageGroupeRabais surFondRouge sx={{ justifyContent: 'flex-start', textAlign: 'left' }} />
            </Box>
            <Button
              component={Link}
              to="/inscription-groupe"
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              sx={{
                flexShrink: 0,
                backgroundColor: '#fff',
                color: '#c62828',
                fontWeight: 700,
                px: 3, py: 1.1,
                borderRadius: '50px',
                textTransform: 'none',
                fontSize: '0.88rem',
                whiteSpace: 'nowrap',
                '&:hover': { backgroundColor: '#fce4ec', transform: 'translateY(-2px)' },
                boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
              }}
            >
              S'inscrire en groupe
            </Button>
          </Box>

          {/* Catégories */}
          {!loading && categoriesVisibles.length > 0 && (
            <Box sx={{ mb: 6 }}>
              <SectionLabel>Parcourir par thème</SectionLabel>
              <SectionTitle variant="h4">Catégories de formations</SectionTitle>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: 2,
                  // Des cartes carrées de 240 px au plus : peu de catégories par
                  // rangée ne doivent pas donner des tuiles géantes.
                  maxWidth: `calc(${parRangee} * 240px + ${parRangee - 1} * 16px)`,
                  mx: 'auto',
                }}
              >
                {categoriesVisibles.map(({ cat, count }) => (
                  <Box key={cat.id} sx={{ flex: `0 0 calc((100% - ${parRangee - 1} * 16px) / ${parRangee})`, minWidth: 0 }}>
                    <CategoryCard category={cat} count={count} onClick={onFilterByCategory} isSelected={selectedCategory === cat.id} />
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Ancre du défilement automatique ; la marge compense la barre de
              navigation collante (72 px) et laisse un peu d'air au-dessus. */}
          <Box ref={catalogueRef} sx={{ scrollMarginTop: '96px' }} />

          {/* Barre filtrage actif */}
          {selectedCategory && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, p: 2, borderRadius: '14px', background: 'rgba(198,40,40,0.07)', border: '1px solid rgba(198,40,40,0.15)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#c62828' }} />
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#c62828' }}>
                  Catégorie : {selectedCatName}
                </Typography>
              </Box>
              <Button size="small" onClick={() => setSelectedCategory(null)}
                sx={{ color: '#c62828', fontWeight: 600, textTransform: 'none', fontSize: '0.82rem', '&:hover': { background: 'rgba(198,40,40,0.1)' } }}>
                × Voir toutes
              </Button>
            </Box>
          )}

          {/* Titre section formations */}
          <SectionLabel>Catalogue complet</SectionLabel>
          <SectionTitle variant="h4">
            {selectedCategory ? `Formations : ${selectedCatName}` : 'Découvrez nos formations'}
          </SectionTitle>

          {/* Contenu */}
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 10, color: '#aaa' }}>
              <SchoolIcon sx={{ fontSize: 48, mb: 2, opacity: 0.3 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Chargement des formations…</Typography>
            </Box>
          ) : filteredFormations.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, background: '#fff', borderRadius: '20px', border: '1px solid rgba(0,0,0,0.06)' }}>
              <Typography variant="h6" sx={{ color: '#c62828', mb: 1 }}>Aucune formation disponible</Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>Contactez-nous pour plus d'informations.</Typography>
            </Box>
          ) : (
            <>
              {selectedCategory ? (
                <Grid container spacing={3}>
                  {filteredFormations.map(f => (
                    <Grid key={f.id} size={{ xs: 12, sm: 6, md: 4 }}>
                      <FormationCard formation={f} onFilterByCategory={onFilterByCategory} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <>
                  {categories.filter(c => groupedByCategoryId[c.id]).map(cat => (
                    <CategoryBox key={cat.id}>
                      {/* En-tête catégorie */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, pb: 2.5, borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
                        <Box sx={{ width: 44, height: 44, borderRadius: '12px', background: `${cat.couleur || '#c62828'}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: cat.couleur || '#c62828', flexShrink: 0 }}>
                          <SchoolIcon />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a1a1a', fontSize: '1.15rem' }}>
                            {cat.nom}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#999' }}>
                            {groupedByCategoryId[cat.id].length} formation{groupedByCategoryId[cat.id].length > 1 ? 's' : ''}
                          </Typography>
                        </Box>
                        <Button size="small" onClick={() => onFilterByCategory(cat.id)}
                          sx={{ color: cat.couleur || '#c62828', fontWeight: 600, fontSize: '0.78rem', textTransform: 'none', border: `1px solid ${cat.couleur || '#c62828'}33`, borderRadius: '20px', px: 1.5, '&:hover': { background: `${cat.couleur || '#c62828'}10` } }}>
                          Voir uniquement →
                        </Button>
                      </Box>
                      <Grid container spacing={2.5}>
                        {groupedByCategoryId[cat.id].map(f => (
                          <Grid key={f.id} size={{ xs: 12, sm: 6, md: 4 }}>
                            <FormationCard formation={f} onFilterByCategory={onFilterByCategory} />
                          </Grid>
                        ))}
                      </Grid>
                    </CategoryBox>
                  ))}

                  {autres.length > 0 && (
                    <CategoryBox>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, pb: 2.5, borderBottom: '1px solid rgba(0,0,0,0.07)' }}>
                        <Box sx={{ width: 44, height: 44, borderRadius: '12px', background: 'rgba(198,40,40,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c62828', flexShrink: 0 }}>
                          <GroupsIcon />
                        </Box>
                        <Box>
                          <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a1a1a', fontSize: '1.15rem' }}>Autres formations</Typography>
                          <Typography variant="caption" sx={{ color: '#999' }}>{autres.length} formation{autres.length > 1 ? 's' : ''} sans catégorie spécifique</Typography>
                        </Box>
                      </Box>
                      <Grid container spacing={2.5}>
                        {autres.map(f => (
                          <Grid key={f.id} size={{ xs: 12, sm: 6, md: 4 }}>
                            <FormationCard formation={f} onFilterByCategory={onFilterByCategory} />
                          </Grid>
                        ))}
                      </Grid>
                    </CategoryBox>
                  )}
                </>
              )}
            </>
          )}

          {/* Bannière certifications */}
          {!loading && (
            <Box sx={{
              mt: 5,
              p: { xs: 4, md: 5 },
              borderRadius: '24px',
              background: 'linear-gradient(135deg, #c62828, #8e0000)',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 20px 50px rgba(198,40,40,0.25)',
              '&::before': { content: '""', position: 'absolute', top: '-60px', right: '-60px', width: 220, height: 220, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' },
            }}>
              <WorkspacePremiumIcon sx={{ fontSize: 44, color: 'rgba(255,255,255,0.25)', mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#fff', mb: 1.5 }}>
                Toutes nos formations sont certifiées
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.85)', mb: 3.5, maxWidth: 560, mx: 'auto', lineHeight: 1.75 }}>
                Certification DEA incluse · Formateurs diplômés · Matériel fourni · Reconnues Croix-Rouge canadienne
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                <Button component={Link} to="/contact" variant="contained"
                  sx={{ background: '#fff', color: '#c62828', fontWeight: 800, px: 4, py: 1.4, borderRadius: '50px', '&:hover': { background: 'rgba(255,255,255,0.92)' } }}>
                  Demander un devis
                </Button>
                <Button component={Link} to="/about" variant="outlined"
                  sx={{ borderColor: 'rgba(255,255,255,0.6)', color: '#fff', fontWeight: 700, px: 4, py: 1.4, borderRadius: '50px', borderWidth: 2, '&:hover': { borderWidth: 2, borderColor: '#fff', background: 'rgba(255,255,255,0.1)' } }}>
                  En savoir plus
                </Button>
              </Stack>
            </Box>
          )}

        </Wrap>
      </PageWrap>

      <Fade in={showScrollButton}>
        <ScrollBtn onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <KeyboardArrowUpIcon />
        </ScrollBtn>
      </Fade>
    </>
  );
};

export default Formations;
